import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, CompareArrows as CompareIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Enhanced comparison tray for the universal comparator.
 * Solid background, prominent primary CTA, clear count message.
 */
const ComparatorTray = ({ selectedItems, onRemove, onClear, onCompare, nameField }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const count = selectedItems.length;

  return (
    <Slide direction="up" in={count >= 1} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1299,
          bgcolor: 'rgba(31,58,95,0.92)',
          backdropFilter: 'blur(10px)',
          color: 'primary.contrastText',
          boxShadow: '0 -1px 12px rgba(0,0,0,0.10)',
          px: { xs: 2, md: 4 },
          py: 1.25,
        }}
        role="region"
        aria-label={t('comparatorTray.ariaLabel')}
      >
        <Box
          sx={{
            maxWidth: 'lg',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {isMobile ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareIcon sx={{ fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('comparatorTray.compareCount', { count })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={onClear}
                  sx={{ textTransform: 'none', color: 'primary.contrastText', opacity: 0.8, minWidth: 0 }}
                >
                  {t('comparatorTray.clear')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={count < 2}
                  onClick={onCompare}
                  sx={{
                    bgcolor: 'secondary.main',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    '&.Mui-disabled': { bgcolor: 'neutral.400', color: 'neutral.0' },
                  }}
                >
                  {t('comparatorTray.compareNow')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <CompareIcon sx={{ fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {t('comparatorTray.compareOf4', { count })}
                </Typography>
                {selectedItems.map((item) => (
                  <Chip
                    key={item.id}
                    label={item[nameField] || item.name}
                    size="small"
                    onDelete={() => onRemove(item)}
                    deleteIcon={<CloseIcon sx={{ fontSize: '0.9rem', color: 'inherit' }} />}
                    sx={{
                      fontWeight: 500,
                      maxWidth: 180,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'primary.contrastText',
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                      '& .MuiChip-deleteIcon': { color: 'primary.contrastText', opacity: 0.7, '&:hover': { opacity: 1 } },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                <Button
                  size="small"
                  onClick={onClear}
                  sx={{ textTransform: 'none', color: 'primary.contrastText', opacity: 0.8 }}
                >
                  {t('comparatorTray.clear')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={count < 2}
                  onClick={onCompare}
                  sx={{
                    bgcolor: 'secondary.main',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    fontSize: '0.85rem',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    '&.Mui-disabled': { bgcolor: 'neutral.400', color: 'neutral.0' },
                  }}
                >
                  {t('comparatorTray.compareNow')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Slide>
  );
};

export default ComparatorTray;
